import * as React from 'react';
import { Star, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Review {
  id: string;
  authorName: string;
  rating: number;
  text?: string;
  createdAt: string;
}

interface RatingDistribution {
  /** Index 0 = 1 star, index 4 = 5 stars */
  counts: [number, number, number, number, number];
  total: number;
  average: number;
}

interface RatingInputProps {
  /** Controlled value 0-5 (0 = no selection) */
  value: number;
  onChange: (rating: number) => void;
  /** Disable interaction */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface ReviewFormProps {
  /** Whether a review is being submitted */
  isSubmitting?: boolean;
  /** Callback with rating (1-5) and optional text */
  onSubmit: (rating: number, text: string) => void;
  className?: string;
}

interface RatingAggregateProps {
  distribution: RatingDistribution;
  className?: string;
}

interface ReviewListProps {
  reviews: Review[];
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Interactive star rating input                                      */
/* ------------------------------------------------------------------ */

/**
 * RatingInput
 * -----------
 * Interactive star selector. Stars fill on hover (preview) and on
 * click (selection). Uses ochre for filled stars, hairline-strong
 * for empty.
 *
 * Keyboard accessible: arrow keys to adjust, Enter to confirm.
 * Announces value via aria-valuenow on the slider role.
 */
export function RatingInput({
  value,
  onChange,
  disabled = false,
  size = 'md',
  className,
}: RatingInputProps) {
  const [hovered, setHovered] = React.useState(0);
  const display = hovered || value;

  const starSize =
    size === 'sm'
      ? 'h-4 w-4'
      : size === 'lg'
        ? 'h-7 w-7'
        : 'h-5 w-5';

  const gap = size === 'lg' ? 'gap-1.5' : 'gap-1';

  return (
    <div
      role="slider"
      aria-label="Calificacion"
      aria-valuenow={value}
      aria-valuemin={1}
      aria-valuemax={5}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          onChange(Math.min(5, value + 1));
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          onChange(Math.max(1, value - 1));
        }
      }}
      className={cn(
        'inline-flex items-center',
        gap,
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)] rounded',
        className,
      )}
    >
      {Array.from({ length: 5 }, (_, i) => {
        const starIndex = i + 1;
        const isFilled = starIndex <= display;
        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            tabIndex={-1}
            aria-label={`${starIndex} estrella${starIndex > 1 ? 's' : ''}`}
            onMouseEnter={() => !disabled && setHovered(starIndex)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => !disabled && onChange(starIndex)}
            className={cn(
              'transition-all duration-100',
              !disabled && 'hover:scale-110 active:scale-95',
            )}
          >
            <Star
              className={cn(
                starSize,
                'transition-colors duration-100',
                isFilled
                  ? 'fill-[var(--color-ochre)] text-[var(--color-ochre)]'
                  : 'fill-none text-[var(--color-hairline-strong)]',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Review submission form                                             */
/* ------------------------------------------------------------------ */

/**
 * ReviewForm
 * ----------
 * Simple review form: star selector + optional short text.
 *
 * Layout:
 * ┌──────────────────────────────────┐
 * │  "Como fue tu experiencia?"      │
 * │  [star] [star] [star] [star] [star]  │
 * │                                  │
 * │  ┌──────────────────────────┐    │
 * │  │ Optional comment...      │    │
 * │  └──────────────────────────┘    │
 * │                    [Enviar]      │
 * └──────────────────────────────────┘
 */
export function ReviewForm({
  isSubmitting = false,
  onSubmit,
  className,
}: ReviewFormProps) {
  const [rating, setRating] = React.useState(0);
  const [text, setText] = React.useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) return;
    onSubmit(rating, text.trim());
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5',
        className,
      )}
    >
      <p className="text-sm font-semibold text-[var(--color-ink)]">
        Como fue tu experiencia?
      </p>

      <div className="mt-3">
        <RatingInput value={rating} onChange={setRating} size="lg" />
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="Agrega un comentario (opcional)"
        className={cn(
          'mt-4 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)]',
          'shadow-[inset_0_1px_0_rgba(0,0,0,0.02)] transition-[border-color,box-shadow] duration-150 ease-out resize-y min-h-[72px]',
          'focus-visible:outline-none focus-visible:border-[var(--color-primary)] focus-visible:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]',
        )}
      />
      <div className="mt-1 flex justify-end">
        <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
          {text.length}/500
        </span>
      </div>

      <div className="mt-3 flex justify-end">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={rating === 0 || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            'Enviar resena'
          )}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Aggregate rating with distribution bars                            */
/* ------------------------------------------------------------------ */

/**
 * RatingAggregate
 * ---------------
 * Shows the overall rating prominently with a horizontal bar
 * distribution breakdown.
 *
 * Layout:
 * ┌──────────────────────────────────┐
 * │  4.7  ★★★★★  (124 resenas)      │
 * │                                  │
 * │  5 ★  ████████████████░░  78     │
 * │  4 ★  ██████████░░░░░░░░  32     │
 * │  3 ★  ███░░░░░░░░░░░░░░░   9    │
 * │  2 ★  █░░░░░░░░░░░░░░░░░   3    │
 * │  1 ★  ░░░░░░░░░░░░░░░░░░   2    │
 * └──────────────────────────────────┘
 */
export function RatingAggregate({
  distribution,
  className,
}: RatingAggregateProps) {
  const { counts, total, average } = distribution;
  const maxCount = Math.max(...counts, 1);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary row */}
      <div className="flex items-center gap-4">
        <p className="font-display text-4xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
          {average.toFixed(1)}
        </p>
        <div>
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={cn(
                  'h-4 w-4',
                  i < Math.round(average)
                    ? 'fill-[var(--color-ochre)] text-[var(--color-ochre)]'
                    : 'fill-none text-[var(--color-hairline-strong)]',
                )}
              />
            ))}
          </div>
          <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
            {total} resena{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Distribution bars — 5 stars down to 1 */}
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((starNum) => {
          const count = counts[starNum - 1] ?? 0;
          const pct = total > 0 ? (count / maxCount) * 100 : 0;
          return (
            <div key={starNum} className="flex items-center gap-2">
              <span className="w-4 text-right text-[0.6875rem] font-medium text-[var(--color-ink-muted)]">
                {starNum}
              </span>
              <Star className="h-3 w-3 fill-[var(--color-ochre)] text-[var(--color-ochre)]" />
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-paper-deep)]">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-[var(--color-ochre)] transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-8 text-right text-[0.6875rem] font-medium text-[var(--color-ink-faint)] tabular">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Individual review                                                  */
/* ------------------------------------------------------------------ */

function ReviewItem({ review }: { review: Review }) {
  const initials = review.authorName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  const dateStr = new Date(review.createdAt).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="flex gap-3 py-4">
      <Avatar className="h-8 w-8 shrink-0 text-[0.5rem]">
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-[var(--color-ink)]">
            {review.authorName}
          </span>
          <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
            {dateStr}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }, (_, i) => (
            <Star
              key={i}
              className={cn(
                'h-3 w-3',
                i < review.rating
                  ? 'fill-[var(--color-ochre)] text-[var(--color-ochre)]'
                  : 'fill-none text-[var(--color-hairline-strong)]',
              )}
            />
          ))}
        </div>
        {review.text && (
          <p className="text-[0.8125rem] leading-relaxed text-[var(--color-ink-soft)]">
            {review.text}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Review list                                                        */
/* ------------------------------------------------------------------ */

/**
 * ReviewList
 * ----------
 * Vertical list of individual reviews, separated by hairline dividers.
 */
export function ReviewList({ reviews, className }: ReviewListProps) {
  if (reviews.length === 0) {
    return (
      <div className={cn('py-8 text-center', className)}>
        <p className="text-sm text-[var(--color-ink-muted)]">
          Aun no hay resenas
        </p>
      </div>
    );
  }

  return (
    <div className={cn('divide-y divide-[var(--color-hairline)]', className)}>
      {reviews.map((review) => (
        <ReviewItem key={review.id} review={review} />
      ))}
    </div>
  );
}
